import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { isPriceResultsPage } from '@/utils/urls';
import { isUserAtLeastPartiallyRegistered, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { LinkBody } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { openCompleteAccountSignupModalFromChecker } from '@/redux/modules/account/shouldLogin/shouldLogin.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import { useTrackItemCardClickedPriceResults } from '@/hooks/kameleoon/tracking/useTrackPriceResultsPage';
import { v4 as uuidv4 } from 'uuid';
import DescriptionLink from '@/components/GridItemCard/Card/components/DescriptionLink/DescriptionLink';
import ImageRow from '@/components/ItemCard/ImageRow';
import ItemCardBadge, {
    StyledFeaturedBadge,
} from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import ms from 'ms';
import React, { useCallback, useMemo, useState } from 'react';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styled from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const UpcomingSalesHistoryCard: ItemCardComponent = ({ className, dimension, itemId }) => {
    const dispatch = useAppDispatch();
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const DESKTOP_LINE_COUNT = 1;
    const MOBILE_LINE_COUNT = 2;

    const { buyNowStatus, catalogStatus, isLocked, isPassed, isSold, saleStartTs } = useAppSelector((state) =>
        getItemSummary(state, itemId)
    );
    const [hoveringLink, setHoveringLink] = useState(false);
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);
    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;

    const isBuyNow = buyNowStatus === BuyNowStatus.Available;

    const { pathname } = useLocation();
    const isOnPriceResultsPage = isPriceResultsPage(pathname);
    const trackItemCardClicked = useTrackItemCardClickedPriceResults();

    const isUserPartiallyRegistered = useAppSelector(isUserAtLeastPartiallyRegistered);
    const isUserFullyLoggedIn = useAppSelector(isUserLoggedIn);
    const guid = uuidv4();
    const modalContext: ModalContext = useMemo(
        () => ({
            eventContext: EventContext.seeSoldPrice,
            id: itemId,
            type: CompleteAccountModalContext.SeeSoldPrice,
        }),
        [itemId]
    );

    const handleSeeSalePrice = useCallback(() => {
        if (isUserFullyLoggedIn) {
            return;
        }
        return dispatch(
            openCompleteAccountSignupModalFromChecker({
                modalContext,
                trigger: guid,
            })
        );
    }, [dispatch, guid, isUserFullyLoggedIn, modalContext]);

    // Track item card clicked on price results page for experiment
    const onNavigate = useCallback(() => {
        if (isOnPriceResultsPage) {
            trackItemCardClicked();
        }
    }, [isOnPriceResultsPage, trackItemCardClicked]);

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const auctionDateInfo = isBuyNow ? (
        <BodySecondary color="secondary">
            <FormattedMessage id="buy_now" />
        </BodySecondary>
    ) : (
        <ItemCardShortDate
            allowBidding
            itemId={itemId}
            showDateWithoutYear={showDateWithoutYear}
            showOnlyYear={isSold || isPassed || isPending}
        />
    );

    return (
        <StyledCard data-testid={`upcoming-sales-price-results-card-${itemId}`}>
            <StyledExpansionContainer data-testid="expansion-container">
                <UpcomingSalesPriceResultsCardGrid
                    $dimension={dimension}
                    className={className}
                    data-testid="upcoming-sales-price-results-card-grid"
                >
                    <StyledImageContainer
                        data-testid="image-container"
                        onMouseEnter={() => {
                            setHoveringLink(true);
                        }}
                        onMouseLeave={() => {
                            setHoveringLink(false);
                        }}
                    >
                        <StyledItemCardBadge
                            itemId={itemId}
                            savedItemsCard
                        />
                        <StyledFavoriteButton>
                            <ItemCardSaveIcon itemId={itemId} />
                        </StyledFavoriteButton>
                        <PlacedItemCardImage
                            dimension={dimension}
                            itemId={itemId}
                            onNavigate={onNavigate}
                        />
                    </StyledImageContainer>
                    <PlacedDetailsForNewMobileGrid>
                        <PlacedItemInfo>
                            <StyledDateInfo>{auctionDateInfo}</StyledDateInfo>
                        </PlacedItemInfo>
                        <PlacedItemCardTitle
                            isHovered={hoveringLink}
                            itemId={itemId}
                            lineCount={isMobile ? MOBILE_LINE_COUNT : DESKTOP_LINE_COUNT}
                            onMouseEnter={() => {
                                setHoveringLink(true);
                            }}
                            onMouseLeave={() => {
                                setHoveringLink(false);
                            }}
                            onNavigate={onNavigate}
                            testid="itemCardTitle"
                        />
                        <PlacedItemDescription>
                            <DescriptionLink
                                increaseLineHeight
                                increaseTextSize
                                isHovered={hoveringLink}
                                itemId={itemId}
                                onMouseEnter={() => {
                                    setHoveringLink(true);
                                }}
                                onMouseLeave={() => {
                                    setHoveringLink(false);
                                }}
                                onNavigate={onNavigate}
                            />
                        </PlacedItemDescription>
                        <PlacedSeeSoldPrice>
                            {isUserPartiallyRegistered && isUserFullyLoggedIn ? (
                                <ItemCardPriceRow
                                    isHovered={hoveringLink}
                                    itemId={itemId}
                                    onMouseEnter={() => {
                                        setHoveringLink(true);
                                    }}
                                    onMouseLeave={() => {
                                        setHoveringLink(false);
                                    }}
                                    onNavigate={onNavigate}
                                />
                            ) : (
                                <ShouldLogInContainer
                                    modalContext={modalContext}
                                    requireLogin
                                    runFunction={handleSeeSalePrice}
                                >
                                    <SeeSalePriceLink>
                                        <FormattedMessage id="price_gateway_card" />
                                    </SeeSalePriceLink>
                                </ShouldLogInContainer>
                            )}
                        </PlacedSeeSoldPrice>
                    </PlacedDetailsForNewMobileGrid>
                </UpcomingSalesPriceResultsCardGrid>
            </StyledExpansionContainer>
        </StyledCard>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 180, mobile: 164 },
    ItemCardComponent: UpcomingSalesHistoryCard,
};

export default info;

const StyledCard = styled.article`
    position: relative;
`;

const StyledExpansionContainer = styled.section`
    margin: 0 auto;
`;

const PlacedItemCardImage = styled(ImageRow)`
    grid-area: itemimage;
`;

const PlacedDetailsForNewMobileGrid = styled.div`
    display: grid;
    overflow: hidden;
    grid-area: alldetailsformobilelayout;
    grid-template-areas: '.' 'iteminfo' 'title' '.' 'itemdescription' '.' 'seesoldprice';
    grid-template-rows: 6px 28px min-content 7px min-content 7px;
    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-areas: '.' 'iteminfo' 'title' '.' 'itemdescription' '.' 'seesoldprice';
        grid-template-rows: 8px 28px min-content 8px min-content 8px;
    }
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const StyledItemCardBadge = styled(ItemCardBadge)`
    ${StyledFeaturedBadge} {
        top: 8px;
        left: 8px;
    }
`;

const PlacedItemInfo = styled.div`
    grid-area: iteminfo;
`;

const PlacedSeeSoldPrice = styled.div`
    grid-area: seesoldprice;
`;

const StyledDateInfo = styled.span`
    width: 100%;
    line-height: 18px;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-area: title;
`;

const PlacedItemDescription = styled.div`
    grid-area: itemdescription;
`;

const StyledImageContainer = styled.div`
    position: relative;
`;

const UpcomingSalesPriceResultsCardGrid = styled.section<{
    $dimension: number;
}>`
    display: grid;
    grid-template-columns: ${({ $dimension }) => $dimension}px;
    grid-template-rows: ${({ $dimension }) => $dimension}px;
    grid-template-areas:
        'itemimage'
        '.'
        'alldetailsformobilelayout';
    justify-content: center;
`;

const SeeSalePriceLink = styled(LinkBody)`
    text-decoration: none;
`;
